import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getReportsList } from '../../../models/reportsModel';

function ReportsList() {
  const [loading, setLoading] = useState(true);
  const [reports, setReports] = useState([]);

  useEffect(() => {
    fetchReports();
  }, []);

  const fetchReports = async () => {
    try {
      setLoading(true);
      // For now, we'll use a static list since we know the available reports
      const staticReports = [
        {
          id: 'chat',
          title: 'Chat Activity',
          description: 'View chat activity reports with time analysis and response metrics',
          icon: '💬',
          category: 'Communication',
          path: '/reports/chat'
        },
        {
          id: 'requisitions',
          title: 'Requisitions',
          description: 'View requisitions reports with filtering and analysis',
          icon: '📋',
          category: 'Procurement',
          path: '/reports/requisitions'
        },
        {
          id: 'pending_requisitions',
          title: 'Pending Requisitions',
          description: 'View pending requisitions requiring attention',
          icon: '⏳',
          category: 'Procurement',
          path: '/reports/pending_requisitions'
        },
        {
          id: 'rfq',
          title: 'RFQ Reports',
          description: 'View Request for Quotation reports and analysis',
          icon: '💰',
          category: 'Procurement',
          path: '/reports/rfq'
        },
        {
          id: 'user',
          title: 'User Activity',
          description: 'Track user activity and system usage patterns',
          icon: '👥',
          category: 'Administration',
          path: '/reports/user'
        },
        {
          id: 'changes',
          title: 'System Changes',
          description: 'View system changes and audit trail',
          icon: '🔄',
          category: 'Administration',
          path: '/reports/changes'
        },
        {
          id: 'helpdesk',
          title: 'Helpdesk',
          description: 'View helpdesk tickets and support metrics',
          icon: '🎫',
          category: 'Support',
          path: '/reports/helpdesk'
        },
        {
          id: 'procurement',
          title: 'Procurement',
          description: 'Overall procurement process reports',
          icon: '🛒',
          category: 'Procurement',
          path: '/reports/procurement'
        },
        {
          id: 'automated_activity',
          title: 'Automated Activity',
          description: 'View automated system activity and processes',
          icon: '🤖',
          category: 'System',
          path: '/reports/automated_activity'
        },
        {
          id: 'mail_log',
          title: 'Mail Log',
          description: 'View email delivery logs and status',
          icon: '📧',
          category: 'System',
          path: '/reports/mail_log'
        }
      ];
      setReports(staticReports);
    } catch (error) {
      console.error('Error fetching reports:', error);
    } finally {
      setLoading(false);
    }
  };

  const groupedReports = reports.reduce((groups, report) => {
    const category = report.category;
    if (!groups[category]) {
      groups[category] = [];
    }
    groups[category].push(report);
    return groups;
  }, {});

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-gray-800">Reporting Dashboard</h1>
        <p className="text-gray-600">Access comprehensive reports and analytics</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6 mb-8">
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-blue-100 text-blue-600 mr-4">
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
            </div>
            <div>
              <p className="text-3xl font-semibold text-gray-800">{reports.length}</p>
              <p className="text-gray-600">Available Reports</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-green-100 text-green-600 mr-4">
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
              </svg>
            </div>
            <div>
              <p className="text-3xl font-semibold text-gray-800">{Object.keys(groupedReports).length}</p>
              <p className="text-gray-600">Report Categories</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-purple-100 text-purple-600 mr-4">
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
            </div>
            <div>
              <p className="text-3xl font-semibold text-gray-800">Real-time</p>
              <p className="text-gray-600">Data Updates</p>
            </div>
          </div>
        </div>
      </div>

      {Object.entries(groupedReports).map(([category, categoryReports]) => (
        <div key={category} className="mb-8">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">{category} Reports</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {categoryReports.map((report) => (
              <Link
                key={report.id}
                to={report.path}
                className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow duration-200 border border-gray-200 hover:border-blue-300"
              >
                <div className="flex items-start">
                  <div className="text-3xl mr-4">{report.icon}</div>
                  <div className="flex-1">
                    <h3 className="text-lg font-medium text-gray-900 mb-2">{report.title}</h3>
                    <p className="text-gray-600 text-sm mb-3">{report.description}</p>
                    <div className="flex items-center text-blue-600 text-sm">
                      <span>View Report</span>
                      <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      ))}

      {reports.length === 0 && (
        <div className="bg-white rounded-lg shadow-md p-8 text-center">
          <div className="text-gray-400 mb-4">
            <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Reports Available</h3>
          <p className="text-gray-600">Reports will appear here when they become available.</p>
        </div>
      )}
    </div>
  );
}

export default ReportsList; 